import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { AXIS_TICK, CHART, GRID_PROPS } from '../../config/chartTheme';
import type { TourismBucket, TourismDimension } from '../../api/tourism';

export const DIMENSION_LABELS: Record<TourismDimension, string> = {
  neighborhood: 'Ortsteil',
  listing_type: 'Objekttyp',
  offer_provider: 'Anbieter',
  price: 'Preisklasse',
  size: 'Größe',
  guests: 'Gästezahl',
  bedrooms: 'Schlafzimmer',
  rating: 'Bewertung',
  zipcode: 'PLZ',
  gruppe: 'Gruppe',
};

/** Ab hier wird der Rest gebündelt – mehr Kategorien liest niemand ab. */
const MAX_BARS = 8;

interface Row {
  key: string;
  occupancyPct: number;
  listings: number;
  isOther: boolean;
}

export function buildBreakdownRows(buckets: TourismBucket[]): {
  rows: Row[];
  foldedCount: number;
} {
  const usable = buckets.filter(b => b.occupancy != null);
  const head = usable.slice(0, MAX_BARS);
  const tail = usable.slice(MAX_BARS);

  const rows: Row[] = head.map(b => ({
    key: b.key,
    occupancyPct: Math.round((b.occupancy ?? 0) * 1000) / 10,
    listings: b.listings,
    isOther: false,
  }));

  if (tail.length > 0) {
    // Gewichtet über die bekannten Tage zusammenfassen – ein Mittel der
    // Prozentwerte würde kleine Segmente überbewerten.
    const known = tail.reduce((sum, b) => sum + b.known_days, 0);
    const unavailable = tail.reduce((sum, b) => sum + b.unavailable_days, 0);
    rows.push({
      key: `Übrige (${tail.length})`,
      occupancyPct: known ? Math.round((unavailable / known) * 1000) / 10 : 0,
      listings: tail.reduce((sum, b) => sum + b.listings, 0),
      isOther: true,
    });
  }

  return { rows, foldedCount: tail.length };
}

export default function BreakdownChart({
  buckets,
  referencePct,
}: {
  buckets: TourismBucket[];
  /** Ø der Gesamtstadt als Bezugslinie. */
  referencePct: number | null;
}) {
  const { rows } = buildBreakdownRows(buckets);
  if (rows.length === 0) {
    return <p className="text-sm text-gray-400 py-6 text-center">Keine Daten für diese Auswahl.</p>;
  }

  const height = Math.max(160, rows.length * 34 + 28);

  return (
    <ResponsiveContainer width="100%" height={height}>
      {/* top-Margin schafft Platz für das Label der Ø-Linie — ohne ihn wird es
          abgeschnitten und die Linie bliebe unerklärt. */}
      <BarChart data={rows} layout="vertical" margin={{ top: 16, right: 56, bottom: 0, left: 8 }}>
        <CartesianGrid {...GRID_PROPS} horizontal={false} />
        <XAxis
          type="number"
          domain={[0, 100]}
          unit=" %"
          tick={AXIS_TICK}
          stroke={CHART.axis}
          tickLine={false}
        />
        <YAxis
          type="category"
          dataKey="key"
          width={140}
          tick={AXIS_TICK}
          stroke={CHART.axis}
          tickLine={false}
        />
        <Tooltip content={<BreakdownTooltip />} cursor={{ fill: '#0b0b0b', fillOpacity: 0.03 }} />

        {referencePct != null && (
          <ReferenceLine
            x={referencePct}
            stroke={CHART.ink}
            strokeWidth={1}
            label={{
              value: `Ø Stadt ${String(referencePct).replace('.', ',')} %`,
              position: 'top',
              fontSize: 10,
              fill: CHART.ink,
            }}
          />
        )}

        {/* Eine Serie, eine Farbe. Ein Werteverlauf würde die Balkenlänge ein
            zweites Mal kodieren und den einzigen freien Kanal verbrauchen. */}
        <Bar dataKey="occupancyPct" radius={[0, 4, 4, 0]} barSize={18} isAnimationActive={false}>
          {rows.map(row => (
            <Cell key={row.key} fill={row.isOther ? CHART.reference : CHART.bar} />
          ))}
          {/* Direktlabel am Balkenende – außerhalb, damit kurze Balken nicht
              beschnitten werden. */}
          <LabelList
            dataKey="occupancyPct"
            position="right"
            fontSize={11}
            fill={CHART.ink}
            formatter={(value: unknown) =>
              value == null ? '' : `${String(value).replace('.', ',')} %`
            }
          />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

function BreakdownTooltip({ active, payload }: { active?: boolean; payload?: { payload: Row }[] }) {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm px-3 py-2 text-xs">
      <div className="font-semibold text-gray-700">{row.key}</div>
      <div className="mt-1 text-gray-700">
        Belegung {String(row.occupancyPct).replace('.', ',')} %
      </div>
      <div className="text-gray-400">
        {row.listings} {row.listings === 1 ? 'Inserat' : 'Inserate'}
      </div>
    </div>
  );
}
